import React, { useMemo, useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { TriangleDownIcon } from '@modernloop/shared/icons';
import { Button, Menu, MenuItem, MenuItemProps } from '@mui/material';

import { DEFAULT_NUMBER_OF_DAYS } from 'src/store/slices/candidate-availability-options';

export type DayCount = -1 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;

export type DayCountPickerProps = {
  additionalOptions?: MenuItemProps[];
  days: DayCount;
  onChange: (value: DayCount) => void;
};

const DayCountPicker = ({ additionalOptions, days, onChange }: DayCountPickerProps): JSX.Element => {
  const { t } = useTranslation();
  const [showDaysMenu, setShowDaysMenu] = useState(false);

  const DAYS_MENU_OPTION: MenuItemProps[] = useMemo(
    () => [
      {
        id: String(DEFAULT_NUMBER_OF_DAYS),
        value: t('hiring-dashboard-candidates:candidates.dayCountPicker.noMinimumNeeded'),
      },
      ...Array.from({ length: 14 }, (_, i) => ({
        id: String(i + 1),
        value: t('hiring-dashboard-candidates:candidates.dayCountPicker.day', { count: i + 1 }),
      })),
    ],
    [t]
  );
  const daysButtonRef = useRef<HTMLButtonElement>(null);

  const daysString = [...(additionalOptions ?? []), ...DAYS_MENU_OPTION].find(
    (option) => Number(option.id) === days
  )?.value;

  const handleShowDaysMenu = () => setShowDaysMenu(true);
  const handleHideDaysMenu = () => setShowDaysMenu(false);
  const handleSelectDays = (option: MenuItemProps) => {
    if (!option.id || Number.isNaN(option.id)) {
      return;
    }
    onChange(Number(option.id) as DayCount);
    handleHideDaysMenu();
  };

  return (
    <>
      <Button
        data-testid="day-count-picker"
        sx={{
          borderRadius: '6px',
          fontWeight: (theme) => theme.typography.fontWeightRegular,
          minWidth: '200px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        ref={daysButtonRef}
        endIcon={<TriangleDownIcon />}
        onClick={handleShowDaysMenu}
      >
        {daysString}
      </Button>
      {showDaysMenu && (
        <Menu
          id="select-days-menu"
          anchorEl={daysButtonRef.current}
          open={showDaysMenu}
          sx={{
            width: daysButtonRef.current?.clientWidth ? daysButtonRef.current?.clientWidth - 16 : undefined,
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          onClose={handleHideDaysMenu}
        >
          {[...(additionalOptions ?? []), ...DAYS_MENU_OPTION].map((option) => (
            <MenuItem
              data-testid={`day-count-picker-option-${option.id}`}
              key={option.id}
              value={option.id}
              selected={Number(option.id) === days}
              onClick={() => handleSelectDays(option)}
            >
              {option.value}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

export default DayCountPicker;
